/**
 * (Shared since v0.133.0: the extension's stat estimates and the website's fight calculator both use this.)
 *
 * Torn's fight curves, worked out from the official wiki's benchmark tables
 * (wiki.torn.com/wiki/Battle_Stat — Attacking 2.0), which these reproduce exactly:
 *
 *  Hit chance by your speed ÷ their dexterity (R):
 *    1/64 → 0%, 0.1 → 10.93%, 1 → 50%, 10 → 89.07%, 64 → 100%
 *    R ≥ 1: 100 − 50/7 × (8/√R − 1);  R < 1: 50/7 × (8√R − 1)
 *
 *  Damage blocked by their defense ÷ your strength (Q):
 *    1/32 → 0%, 1/8 → 20%, 1 → 50%, 10 → 93.63%, 14 → 100%
 *    Q ≥ 1: 50 + 50 × ln Q / ln 14;  Q < 1: 50 + 50 × ln Q / ln 32
 *
 *  Strength's damage (community-derived, approximate):
 *    7 × (log10(STR/10))² + 27 × log10(STR/10) + 30
 *
 * Each has an inverse, so what happens in a fight says something about the other
 * player's stats: how often you hit them → their dexterity (your speed known);
 * how much a hit is blocked → their defense (your strength known); and the same
 * the other way round for their speed and strength.
 */

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));

/** Chance to hit (0–1) for attacker speed ÷ defender dexterity. */
export function hitChance(ratio: number): number {
  if (!(ratio > 0)) return 0;
  const p = ratio >= 1 ? 100 - (50 / 7) * (8 / Math.sqrt(ratio) - 1) : (50 / 7) * (8 * Math.sqrt(ratio) - 1);
  return clamp(p, 0, 100) / 100;
}

/** The speed ÷ dexterity ratio that gives this hit chance (0–1), within [1/64, 64]. */
export function ratioForHitChance(p: number): number {
  const pc = clamp(p, 0, 1) * 100;
  if (pc >= 50) {
    // 100 − 50/7 (8/√R − 1) = pc  →  8/√R = (100 − pc) × 7/50 + 1
    const s = 8 / (((100 - pc) * 7) / 50 + 1);
    return clamp(s * s, 1, 64);
  }
  // 50/7 (8√R − 1) = pc  →  √R = (pc × 7/50 + 1) / 8
  const s = ((pc * 7) / 50 + 1) / 8;
  return clamp(s * s, 1 / 64, 1);
}

/** Share of damage blocked (0–1) for defender defense ÷ attacker strength. */
export function mitigation(ratio: number): number {
  if (!(ratio > 0)) return 0;
  const m = ratio >= 1 ? 50 + (50 * Math.log(ratio)) / Math.log(14) : 50 + (50 * Math.log(ratio)) / Math.log(32);
  return clamp(m, 0, 100) / 100;
}

/** The defense ÷ strength ratio that blocks this share of damage (0–1), within [1/32, 14]. */
export function ratioForMitigation(m: number): number {
  const mc = clamp(m, 0, 1) * 100;
  if (mc >= 50) return clamp(Math.exp(((mc - 50) / 50) * Math.log(14)), 1, 14);
  return clamp(Math.exp(((mc - 50) / 50) * Math.log(32)), 1 / 32, 1);
}

/** Strength's damage factor (community formula). */
export function strengthDamage(strength: number): number {
  if (!(strength > 10)) return 30;
  const l = Math.log10(strength / 10);
  return 7 * l * l + 27 * l + 30;
}

/** The strength that gives this damage factor. */
export function strengthForDamage(d: number): number {
  // 7l² + 27l + (30 − d) = 0
  const disc = 27 * 27 - 4 * 7 * (30 - d);
  if (disc < 0 || d <= 30) return 10;
  const l = (-27 + Math.sqrt(disc)) / 14;
  return 10 * 10 ** l;
}

/** Damage multipliers by body part (wiki). */
export const BODY_PART: Record<string, number> = {
  head: 3.5,
  throat: 3.5,
  heart: 3.5,
  chest: 2,
  stomach: 2,
  groin: 2,
  'left arm': 1,
  'right arm': 1,
  'left leg': 1,
  'right leg': 1,
  arm: 1,
  leg: 1,
  'left hand': 0.7,
  'right hand': 0.7,
  'left foot': 0.7,
  'right foot': 0.7,
  hand: 0.7,
  foot: 0.7,
};

export function bodyPartMultiplier(part: string | null | undefined): number | null {
  if (!part) return null;
  const p = part.toLowerCase().trim();
  return BODY_PART[p] ?? BODY_PART[p.replace(/^(left|right) /, '')] ?? null;
}

/**
 * The most likely hit chance from `hits` out of `shots`, smoothed toward 50%
 * (as if one extra hit and one extra miss had been seen), so a 3-for-3 fight
 * doesn't read as "always hits".
 */
export function smoothedRate(hits: number, shots: number): number {
  return (hits + 1) / (shots + 2);
}

/**
 * A hit's damage before the defender's defense: strength's damage × weapon damage ÷ 10 × body part × (1 − armor).
 * The same approximate model the extension's estimates use (core/inference.ts); `armor` is a share (0.3 = 30%).
 */
export function rawDamage(strength: number, weaponDamage: number, partMultiplier: number, armor = 0): number {
  if (!(weaponDamage > 0) || !(partMultiplier > 0)) return 0;
  return strengthDamage(strength) * (weaponDamage / 10) * partMultiplier * (1 - clamp(armor, 0, 0.95));
}

// ---------------------------------------------------------------- fight calculator (website, TornStats' ProxiQuality-style)

export interface FightSide {
  strength: number;
  defense: number;
  speed: number;
  dexterity: number;
  /** The weapon's damage stat (as Torn shows it on the item). */
  weaponDamage: number;
  /** Extra damage from perks, merits, education… as a percentage (10 = +10%). */
  damageBonus?: number;
  /** Extra hit chance in percentage points (perks, the weapon's accuracy as you reckon it) — added to the curve's chance. */
  hitBonus?: number;
  /** Armor on the body part the other side hits, as a percentage (Torn shows it on the armor). */
  armor?: number;
  /** Life, for "hits to finish". */
  life?: number;
}

/** The body parts as Torn groups them for damage (wiki), with their multiplier. */
export const PART_GROUPS: ReadonlyArray<{ id: 'head' | 'chest' | 'limbs' | 'extremities'; label: string; x: number }> = [
  { id: 'head', label: 'Head, throat, heart', x: 3.5 },
  { id: 'chest', label: 'Chest, stomach, groin', x: 2 },
  { id: 'limbs', label: 'Arms, legs', x: 1 },
  { id: 'extremities', label: 'Hands, feet', x: 0.7 },
];

export interface FightLine {
  /** Chance to hit (0–1): the speed ÷ dexterity curve, plus the hit bonus. */
  hitChance: number;
  /** From the curve alone. */
  baseHitChance: number;
  /** Share of damage the defender's defense blocks (0–1). */
  mitigation: number;
  /** Damage per landed hit, by body part group (rounded). */
  damage: Record<'head' | 'chest' | 'limbs' | 'extremities', number>;
  /** Damage per turn on a chest hit, counting misses (rounded). */
  perTurnChest: number;
  /** Landed chest hits to take the defender's life to 0 (null: no life given or no damage). */
  hitsToFinish: number | null;
}

/** One side attacking the other: hit chance, what the defense blocks and the damage a hit does to each body part. */
export function fightLine(attacker: FightSide, defender: FightSide): FightLine {
  const base = hitChance(attacker.speed / Math.max(1, defender.dexterity));
  const hit = clamp(base + (attacker.hitBonus ?? 0) / 100, 0, 1);
  const mit = mitigation(defender.defense / Math.max(1, attacker.strength));
  const bonus = 1 + (attacker.damageBonus ?? 0) / 100;
  const armor = (defender.armor ?? 0) / 100;
  const dmg = (x: number) => Math.round(rawDamage(attacker.strength, attacker.weaponDamage, x, armor) * (1 - mit) * bonus);
  const damage = { head: dmg(3.5), chest: dmg(2), limbs: dmg(1), extremities: dmg(0.7) };
  return {
    hitChance: hit,
    baseHitChance: base,
    mitigation: mit,
    damage,
    perTurnChest: Math.round(damage.chest * hit),
    hitsToFinish: defender.life && damage.chest > 0 ? Math.ceil(defender.life / damage.chest) : null,
  };
}
